import { format, addDays, parseISO } from 'date-fns';
import {
  transformDollarToPennies,
  transformPenniesToDollar,
} from 'src/utilities/currency-utils';
import {
  GhPlusSubscriptionFormFields,
  GhPlusSubscriptionFundingFormField,
} from 'src/components/enterprise/features/Corporate/components/Form/types/CorporateForm';
import {
  GhPlusSubscriptionPayloadUI,
  GhPlusSubscriptionResponseUI,
  CorpSubscriptionFundingType,
} from 'src/types/Corp';
import { ghPlusSubscriptionDefaultValues } from 'src/components/enterprise/features/Corporate/components/Form/constants/CorporateDefaultValues';
import {
  extractTrialLengthFromMonthlyFeeSelect,
  extractPriceFromMonthlyFeeSelect,
} from 'src/components/enterprise/features/Corporate/components/Form/GrubhubSeamlessPlusWrapper/MembershipFeeSelectControl/mebershipFeeSelectControl.utils';

export function transformGhPlusSubscriptionUIToModel(
  ghPlusEnabled: boolean,
  ghPlusSubscription: GhPlusSubscriptionFormFields,
  ghPlusSubscriptionRaw?: GhPlusSubscriptionPayloadUI
): GhPlusSubscriptionPayloadUI {
  if (!ghPlusEnabled) {
    return null;
  }

  const { funding, monthlyFeeField, trialLengthDays } = ghPlusSubscription;
  const payloadFunding = setPayloadFundingValue(funding);
  const transformedTrialLengthDays =
    trialLengthDays && parseInt(trialLengthDays, 10);

  if (funding === CorpSubscriptionFundingType.CORP) {
    return {
      ...(ghPlusSubscriptionRaw || {}),
      trialLengthDays:
        transformedTrialLengthDays || ghPlusSubscriptionRaw.trialLengthDays,
      funding: payloadFunding,
      monthlyFee: transformDollarToPennies(monthlyFeeField),
    };
  }

  if (funding === CorpSubscriptionFundingType.EMPLOYEE) {
    if (ghPlusSubscriptionRaw) {
      return ghPlusSubscriptionRaw;
    }

    const extractedMonthlyFee = extractPriceFromMonthlyFeeSelect(
      ghPlusSubscription.monthlyFeeSelect
    );
    const monthlyFee = parseInt(extractedMonthlyFee, 10);
    const extractedTrialLength = extractTrialLengthFromMonthlyFeeSelect(
      ghPlusSubscription.monthlyFeeSelect
    );
    const trialLengthDays = parseInt(extractedTrialLength, 10);
    return {
      trialLengthDays,
      funding: payloadFunding,
      monthlyFee,
    };
  }

  return {
    ...(ghPlusSubscriptionRaw || {}),
    trialLengthDays: 0,
    funding: payloadFunding,
    monthlyFee: 0,
  };
}

export function transformGhPlusSubscriptionModelToUI(
  ghPlusEnabled: boolean,
  ghPlusSubscription: GhPlusSubscriptionResponseUI
): GhPlusSubscriptionFormFields {
  if (!ghPlusEnabled) {
    return ghPlusSubscriptionDefaultValues;
  }

  const { funding, monthlyFee, trialLengthDays, startDate } =
    ghPlusSubscription;

  if (funding === CorpSubscriptionFundingType.EMPLOYEE) {
    const monthlyFeeSelect = `${monthlyFee}-${trialLengthDays}`;
    return {
      startDate,
      funding,
      trialLengthDays: trialLengthDays.toString(),
      monthlyFeeSelect,
    };
  }

  const monthlyFeeField = transformPenniesToDollar(monthlyFee);

  return {
    startDate,
    funding: CorpSubscriptionFundingType.CORP,
    trialLengthDays: trialLengthDays.toString(),
    monthlyFeeField: parseFloat(monthlyFeeField),
  };
}

function setPayloadFundingValue(
  funding: GhPlusSubscriptionFundingFormField
): CorpSubscriptionFundingType {
  return CorpSubscriptionFundingType.CORP === funding
    ? CorpSubscriptionFundingType.CORP
    : CorpSubscriptionFundingType.EMPLOYEE;
}

export function getTrialPeriodRange(
  trialLength: string,
  startDate?: string | undefined
): string {
  if (!trialLength) {
    return '';
  }
  const trialLengthValue = parseInt(trialLength, 10);
  const dateFormat = 'MMM d, yyyy';
  const startDateValue = startDate ? parseISO(startDate) : new Date();
  const endDate = addDays(startDateValue, trialLengthValue);
  const startDateFormatted = format(startDateValue, dateFormat);
  const endDateFormatted = format(endDate, dateFormat);

  return `${trialLength} days (${startDateFormatted} - ${endDateFormatted})`;
}
